import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { CoRegulateMark } from '../components/Logo';
import { PrimaryButton, TertiaryButton } from '../components/brand/BrandButton';
import { BrandCard } from '../components/brand/BrandCard';
import { BrandInput } from '../components/brand/BrandInput';
import { Heading, BodyText, SmallText, AccentText } from '../components/brand/BrandTypography';
import SignUpDisclaimer from '../components/SignUpDisclaimer';

const Auth = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { login, register, isAuthenticated } = useAuth();
    
    // Check URL params or state for mode
    const searchParams = new URLSearchParams(location.search);
    const [isLogin, setIsLogin] = useState(searchParams.get('mode') === 'login');
    
    // Get return URL from state or query params
    const getReturnUrl = () => {
        // First check state (preferred)
        if (location.state?.returnTo) {
            return location.state.returnTo;
        }
        // Then check query params (backwards compatibility)
        const redirectParam = searchParams.get('redirect');
        if (redirectParam) {
            return redirectParam;
        }
        // Default to home
        return '/home';
    };
    
    // Redirect already authenticated users - but respect return URL
    useEffect(() => {
        if (isAuthenticated) {
            navigate(getReturnUrl(), { replace: true });
        }
    }, [isAuthenticated, navigate, location.state, location.search]);
    
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: ''
    });
    const [loading, setLoading] = useState(false);
    const [showDisclaimer, setShowDisclaimer] = useState(false);
    const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);

    const toggleMode = () => {
        setIsLogin(!isLogin);
        setFormData({ name: '', email: '', password: '' });
        setDisclaimerAccepted(false);
    };

    const handleChange = (e) => {
        setFormData(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // For sign up, show disclaimer first if not yet accepted
        if (!isLogin && !disclaimerAccepted) {
            setShowDisclaimer(true);
            return;
        }
        
        setLoading(true);
        
        try {
            if (isLogin) {
                await login(formData.email, formData.password);
                // Use return URL from state or query params
                navigate(getReturnUrl());
            } else {
                await register(formData.name, formData.email, formData.password, disclaimerAccepted);
                // Use return URL from state or query params
                navigate(getReturnUrl());
            }
        } catch (error) {
            // Get specific error message from backend or provide helpful defaults
            let message = error.response?.data?.detail;
            
            if (!message) {
                // Provide more helpful error messages based on context
                if (isLogin) {
                    message = 'Invalid email or password. Please check your credentials and try again.';
                } else {
                    message = 'Unable to create account. Please try again.';
                }
            }
            
            // Handle specific error codes
            if (error.response?.status === 401) {
                message = 'Invalid email or password. Please check your credentials and try again.';
            } else if (error.response?.status === 400) {
                message = error.response?.data?.detail || 'Please check your information and try again.';
            } else if (error.response?.status === 500) {
                message = 'Server error. Please try again later.';
            } else if (error.code === 'ECONNABORTED') {
                message = 'Request timed out. Please try again.';
            } else if (error.code === 'ERR_NETWORK' || !error.response) {
                message = 'Unable to connect. Please check your internet connection and try again.';
            }
            
            console.error('Login error:', error.code, error.message);
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    const handleDisclaimerAccept = async () => {
        setDisclaimerAccepted(true);
        setShowDisclaimer(false);
        
        // Now proceed with registration
        setLoading(true);
        try {
            await register(formData.name, formData.email, formData.password, true);
            // Check if we have a return path from assessment
            const returnTo = location.state?.returnTo || '/home';
            navigate(returnTo);
        } catch (error) {
            const message = error.response?.data?.detail || 'Something went wrong';
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    const handleDisclaimerDecline = () => {
        setShowDisclaimer(false);
        toast.info("Take your time. You can sign up when you're ready.");
    };

    return (
        <div className="min-h-screen bg-transparent flex items-center justify-center p-6">
            {/* Sign Up Disclaimer Modal */}
            <SignUpDisclaimer 
                isOpen={showDisclaimer}
                onAccept={handleDisclaimerAccept}
                onDecline={handleDisclaimerDecline}
            />
            
            <div className="w-full max-w-md">
                <TertiaryButton 
                    data-testid="back-to-home-btn"
                    onClick={() => navigate('/landing')}
                    className="mb-10"
                    underline={false}
                >
                    <ArrowLeft className="w-4 h-4" />
                    <span>Back to home</span>
                </TertiaryButton>
                
                <div className="glass-card rounded-3xl p-8">
                    <div className="text-center pb-2">
                        <div className="mb-4 flex justify-center">
                            <CoRegulateMark size={48} />
                        </div>
                        
                        {/* Show message when coming from assessment */}
                        {location.state?.from === 'assessment' && !isLogin && (
                            <div className="mb-4 p-3 rounded-xl bg-[#1D6B6B]/10 border border-[#1D6B6B]/20">
                                <SmallText className="text-[#1D6B6B]">
                                    Great job completing the assessment! Create an account to access your personalized tools.
                                </SmallText>
                            </div>
                        )}
                        
                        <Heading as="h1" size="text-2xl">
                            {isLogin ? 'Welcome back' : 'Create your space'}
                        </Heading>
                        <BodyText className="mt-2">
                            {isLogin 
                                ? 'Your safe space is waiting' 
                                : 'Made for ADHD hearts, shaped by ADHD minds'
                            }
                        </BodyText>
                    </div>
                    
                    <div className="pt-6">
                        <form onSubmit={handleSubmit} className="space-y-5" autoComplete="on">
                            {!isLogin && (
                                <BrandInput
                                    id="name"
                                    name="name"
                                    type="text"
                                    label="Name"
                                    placeholder="What should we call you?"
                                    value={formData.name}
                                    onChange={handleChange}
                                    required={!isLogin}
                                    autoComplete="name"
                                    data-testid="name-input"
                                />
                            )}
                            
                            <BrandInput
                                id="email"
                                name="email"
                                type="email"
                                label="Email"
                                placeholder="your@email.com"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                autoComplete={isLogin ? "username" : "email"}
                                inputMode="email"
                                data-testid="email-input"
                            />
                            
                            <BrandInput
                                id="password"
                                name="password"
                                type="password"
                                label="Password"
                                placeholder={isLogin ? "Enter your password" : "Choose a password"}
                                autoComplete={isLogin ? "current-password" : "new-password"}
                                value={formData.password}
                                onChange={handleChange}
                                required
                                minLength={6}
                                data-testid="password-input"
                            />
                            
                            {isLogin && (
                                <div className="text-right mt-2">
                                    <a 
                                        href="/forgot-password"
                                        className="text-sm text-[#1D6B6B] hover:text-[#145555] transition-colors"
                                        data-testid="forgot-password-link"
                                    >
                                        Forgot password?
                                    </a>
                                </div>
                            )}
                            
                            <PrimaryButton 
                                type="submit" 
                                className="w-full mt-6"
                                size="xl"
                                disabled={loading}
                                data-testid="submit-btn"
                            >
                                {loading ? (
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                ) : (
                                    isLogin ? 'Sign In' : 'Continue'
                                )}
                            </PrimaryButton>
                        </form>
                        
                        {!isLogin && (
                            <SmallText className="text-center mt-4 text-[#7D8E95]">
                                You'll need to review and accept our terms before creating your account
                            </SmallText>
                        )}
                        
                        <div className="mt-8 text-center">
                            <BodyText>
                                {isLogin ? "Don't have an account?" : "Already have an account?"}
                                {' '}
                                <button
                                    type="button"
                                    onClick={toggleMode}
                                    className="text-[#1D6B6B] hover:underline font-medium transition-colors"
                                    data-testid="switch-auth-btn"
                                >
                                    {isLogin ? 'Sign up' : 'Sign in'}
                                </button>
                            </BodyText>
                        </div>
                    </div>
                </div>
                
                <SmallText as="p" className="text-center mt-10">
                    Your data is safe. We never share your information.
                </SmallText>
            </div>
        </div>
    );
};

export default Auth;
